import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Page, PortalSchema } from "../../shared/schema";

interface UsePageRoutingResult {
  /** The currently active page based on URL or first page */
  currentPage: Page | null;
  /** The current page ID */
  currentPageId: string | null;
  /** Navigate to a page by ID - updates the URL */
  navigateToPage: (pageId: string) => void;
  /** Navigate to a page by slug - updates the URL */
  navigateToSlug: (slug: string) => void;
  /** Whether the current URL matches a valid page */
  isValidRoute: boolean;
}

/**
 * Hook for URL-based page routing in the enduser portal.
 *
 * Reads the current page slug from URL params and provides navigation functions
 * that update the URL. This enables deep linking, browser back/forward navigation,
 * and shareable URLs.
 *
 * URL Structure:
 * - "/" -> First page (default)
 * - "/{slug}" -> Page with matching slug
 *
 * @param schema - The portal schema containing pages, or null if not yet loaded
 * @returns Routing state and navigation functions
 */
export function usePageRouting(schema: PortalSchema | null): UsePageRoutingResult {
  const navigate = useNavigate();
  const { pageSlug } = useParams<{ pageSlug?: string }>();

  // Determine current page based on URL slug
  const { currentPage, isValidRoute } = useMemo(() => {
    if (!schema || schema.pages.length === 0) {
      return { currentPage: null, isValidRoute: true };
    }

    const firstPage = schema.pages[0] ?? null;

    // Root URL "/" - show first page
    if (!pageSlug) {
      return { currentPage: firstPage, isValidRoute: true };
    }

    // Find page by slug
    const foundPage = schema.pages.find((p) => p.slug === pageSlug);

    if (foundPage) {
      return { currentPage: foundPage, isValidRoute: true };
    }

    // Invalid slug - return first page but mark as invalid for redirect
    return { currentPage: firstPage, isValidRoute: false };
  }, [schema, pageSlug]);

  // Navigate to a page by ID - updates the URL
  const navigateToPage = useCallback(
    (pageId: string) => {
      if (!schema) return;

      const page = schema.pages.find((p) => p.id === pageId);
      if (!page) return;

      const firstPage = schema.pages[0];
      // Use root "/" for first page, otherwise use the slug
      const targetPath = page.id === firstPage?.id ? "/" : `/${page.slug}`;
      navigate(targetPath);
    },
    [schema, navigate]
  );

  // Navigate to a page by slug - updates the URL
  const navigateToSlug = useCallback(
    (slug: string) => {
      if (!schema) return;

      const firstPage = schema.pages[0];
      // Use root "/" for first page, otherwise use the slug
      const targetPath = slug === firstPage?.slug ? "/" : `/${slug}`;
      navigate(targetPath);
    },
    [schema, navigate]
  );

  return {
    currentPage,
    currentPageId: currentPage?.id ?? null,
    navigateToPage,
    navigateToSlug,
    isValidRoute,
  };
}
